import React, { useEffect, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { Checkbox } from "react-native-paper";
import { DatePickerInput } from "react-native-paper-dates";
import { XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import { StoragePathType } from "@axio/graphql/generated/graphql";
import type { ProcessedFile } from "../../../../hooks/useFileUpload";

interface AssignmentGradingSectionProps {
  existingFeedbackFiles?: Array<{ id: string; fileURL: string }>;
  dueDate?: Date;
}

const validateGradeInput = (value: string) => {
  const valueAsNumber = Number(value);
  if (isNaN(valueAsNumber)) {
    return NaN;
  }
  return Math.max(Math.min(valueAsNumber, 100), 0);
};

const AssignmentGradingSection: React.FC<AssignmentGradingSectionProps> = ({ existingFeedbackFiles, dueDate }) => {
  const { updatedAssignmentPayload, setUpdatedAssignmentPayload } = useGradingCenterSubmissions();
  const [fileURLMap, setFileURLMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingFeedbackFiles) {
      setFileURLMap(Object.fromEntries(existingFeedbackFiles.map((f) => [f.id, f.fileURL])));
    }
  }, [existingFeedbackFiles]);

  const handleFeedbackFilesUploaded = (processedFiles: ProcessedFile[]) => {
    const newFiles = processedFiles.map(({ file, localUploadedFile }) => ({
      fileId: file.id,
      fileName: localUploadedFile.name,
    }));
    setFileURLMap((prev) => ({
      ...prev,
      ...Object.fromEntries(processedFiles.map(({ file }) => [file.id, file.fileURL])),
    }));
    setUpdatedAssignmentPayload((prev) => ({
      ...prev,
      feedbackFiles: [...(prev?.feedbackFiles ?? []), ...newFiles],
    }));
  };

  const handleRemoveFeedbackFile = (fileId: string) => {
    setUpdatedAssignmentPayload({
      ...updatedAssignmentPayload,
      feedbackFiles: updatedAssignmentPayload?.feedbackFiles?.filter((f) => f.fileId !== fileId) ?? [],
    });
  };

  return (
    <View style={styles.gradingSection}>
      <View style={styles.gradingInputs}>
        <View style={styles.gradeInputContainer}>
          <CustomText text="Grade" size="s" weight="bold" style={styles.label} />
          <View style={styles.gradeInputWrapper}>
            <CustomTextInput
              keyboardType="numeric"
              value={updatedAssignmentPayload?.grade == null ? "" : updatedAssignmentPayload.grade.toString()}
              onChangeText={(value) => {
                const grade = validateGradeInput(value);
                if (isNaN(grade)) {
                  return;
                }
                setUpdatedAssignmentPayload({
                  ...updatedAssignmentPayload,
                  grade: value === "" ? null : grade,
                });
              }}
              placeholder="Enter grade"
              style={styles.gradeInput}
            />
            <CustomText text="%" size="s" style={styles.gradeSuffix} useTranslationText={false} />
          </View>
          <CustomText text="Enter a grade from 0 to 100" size="xs" style={styles.gradeHelp} />
        </View>

        <View style={styles.retakeOptionContainer}>
          <View style={styles.checkboxContainer}>
            <Checkbox
              status={updatedAssignmentPayload?.retakeAllowed ? "checked" : "unchecked"}
              onPress={() =>
                setUpdatedAssignmentPayload({
                  ...updatedAssignmentPayload,
                  retakeAllowed: !updatedAssignmentPayload?.retakeAllowed,
                  retakeDeadline:
                    dueDate && new Date(dueDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
                      ? new Date(dueDate)
                      : new Date(),
                })
              }
              theme={{ colors: { primary: "#3B82F6" } }}
            />
            <CustomText text="Allow Retake" size="s" style={styles.checkboxLabel} />
          </View>
          {updatedAssignmentPayload?.retakeAllowed && (
            <View style={styles.retakeOptions}>
              <CustomText text="Retake Deadline" size="s" weight="bold" style={styles.label} />
              <DatePickerInput
                locale="en"
                label={undefined}
                value={updatedAssignmentPayload?.retakeDeadline}
                onChange={(date) => setUpdatedAssignmentPayload({ ...updatedAssignmentPayload, retakeDeadline: date })}
                inputMode="start"
                mode="outlined"
                withDateFormatInLabel={false}
                outlineColor={"#D1D5DB"}
                activeOutlineColor={"#3B82F6"}
                placeholder="MM/DD/YYYY"
                theme={{ roundness: 8 }}
                style={styles.dateInput}
                aria-label="Retake deadline date picker"
                validRange={{
                  startDate:
                    dueDate && new Date(dueDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
                      ? new Date(dueDate)
                      : new Date(),
                }}
              />
            </View>
          )}
        </View>
      </View>

      <View style={styles.feedbackContainer}>
        <CustomText text="Feedback for Student (Optional)" size="s" weight="bold" style={styles.label} />
        <CustomTextInput
          value={updatedAssignmentPayload?.feedback}
          onChangeText={(value) => setUpdatedAssignmentPayload({ ...updatedAssignmentPayload, feedback: value })}
          placeholder="Provide feedback on the student's work..."
          multiline
          numberOfLines={8}
          maxLength={10000}
          style={styles.feedbackTextarea}
        />
        <CustomText
          text="character_count"
          size="xs"
          style={styles.feedbackCharCount}
          interpolation={{ value: updatedAssignmentPayload?.feedback?.length || 0 }}
        />

        <View style={styles.feedbackFilesContainer}>
          <CustomText text="Attach Feedback files" size="s" weight="bold" style={styles.label} />
          <CustomSpacing type="vertical" size="s" />
          <FileUploadComponent
            storagePath={StoragePathType.FeedbackAttachments}
            afterUpload={handleFeedbackFilesUploaded}
            multiple
          />
          {updatedAssignmentPayload?.feedbackFiles?.length > 0 && (
            <View style={styles.uploadedFilesList}>
              {updatedAssignmentPayload.feedbackFiles.map((file) => (
                <View key={file.fileId} style={styles.feedbackFileWrapper}>
                  <FileSubmissionItem
                    file={{ id: file.fileId, fileName: file.fileName, fileURL: fileURLMap[file.fileId] }}
                  />
                  <Pressable
                    onPress={() => handleRemoveFeedbackFile(file.fileId)}
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${file.fileName}`}
                    style={styles.removeButton}>
                    <XIcon size={16} color="#6B7280" />
                  </Pressable>
                </View>
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  gradingSection: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    padding: 24,
    marginBottom: 32,
  },
  gradingInputs: {
    flexDirection: "row",
    gap: 24,
    marginBottom: 24,
  },
  gradeInputContainer: {
    flex: 1,
  },
  gradeInputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
  },
  gradeInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingRight: 40,
    fontSize: 16,
  },
  gradeSuffix: {
    position: "absolute",
    right: 16,
    color: "#6B7280",
    fontWeight: "600",
  },
  gradeHelp: {
    color: "#6B7280",
    marginTop: 6,
  },
  retakeOptionContainer: {
    flex: 1,
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxLabel: {
    color: "#374151",
  },
  retakeOptions: {
    marginTop: 12,
    paddingLeft: 28,
  },
  dateInput: {
    backgroundColor: "#FFFFFF",
    maxHeight: 56,
  },
  feedbackContainer: {
    flex: 1,
  },
  feedbackTextarea: {
    marginTop: 8,
    minHeight: 200,
    textAlignVertical: "top",
  },
  feedbackCharCount: {
    color: "#6B7280",
    textAlign: "right",
    marginTop: 6,
  },
  feedbackFilesContainer: {
    marginTop: 16,
  },
  label: {
    color: "#344054",
    fontWeight: "500",
  },
  uploadedFilesList: {
    marginTop: 12,
    gap: 8,
  },
  feedbackFileWrapper: {
    position: "relative",
  },
  removeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    padding: 4,
    zIndex: 1,
  },
});

export default AssignmentGradingSection;
